import { useState, useEffect } from 'react';
import { Row, Col, Space, message, Form, Select, Dropdown } from 'antd';
import SemesterConfig from "@/config/semester";
import ArchivedConfig from "@/config/archived";
import { getLangList } from '@/lib/i18n';
import { hasPermission } from "@/lib/permissionUtil";
import { showDelConfirm, getOnOkFunction, selectFilterOption, getOptionArray, getDataArray } from "@/lib/antdElementFunction";
import { getCurrentAcademicYear, yearArray } from "@/lib/dateTime";
import ResponsiveTable from '@/components/responsiveTable';
import { ReModal, ReButton } from "@/components/refineElement";
import ModalSemester from './ModalSemester';
import ModalExam from './ModalExam';
import { SemesterType, ExamType, FormatedSemesterType } from './types';

const { getSemestersByYear, createSemester, updateSemesterAndExam, deleteSemester, createExam, deleteExam } = SemesterConfig;
const { updateArchivedTranscript } = ArchivedConfig;

function SemesterManagement() {
  const [semesterArray, setSemesterArray] = useState<SemesterType[]>([]);
  const [formatedSemesterArray, setFormatedSemesterArray] = useState<FormatedSemesterType[]>([]);
  const [semesterInfo, setSemesterInfo] = useState({} as FormatedSemesterType);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [handleType, setHandleType] = useState('');
  const [form] = Form.useForm();
  const semesterT: any = getLangList("semester");
  const errorT: any = getLangList("error");
  const buttonT: any = getLangList("button");
  const menuTitleT: any = getLangList("menuTitle");
  const okSemesterFunction = {
    createSemester: createSemesterFunction,
    updateSemester: updateSemesterFunction,
  };
  const okExamFunction = {
    createExam: createExamFunction,
    updateExam: updateExamFunction,
  };

  const getColumns = () => {
    const getRowSpan = (record: FormatedSemesterType) => {
      const { semesterId, examId } = record;
      const { examList = [] } = semesterArray?.find((item) => item.semesterId === semesterId) ?? {};
      const isFirst = (examList.length === 0) || examList?.[0]?.examId === examId;
      const examCount = examList.length || 1;

      return isFirst ? examCount : 0;
    };

    return [
      {
        title: semesterT.SEMESTER,
        dataIndex: "semesterName",
        onCell: (record: FormatedSemesterType) => ({ rowSpan: getRowSpan(record) })
      },
      {
        title: semesterT.EXAM,
        dataIndex: "examName",
        render: (text: string, record: FormatedSemesterType) => text && (
          <Space>
            {text}
            <ReButton iconName='edit' attributeObject={{ type: 'link' }} onClick={clickExamEdit(record)} />
            <ReButton iconName='delete' attributeObject={{ danger: true, type: 'text' }} onClick={clickExamDelete(record.examId)} />
          </Space>
        ),
      },
      {
        title: buttonT.OPERATE,
        dataIndex: "action",
        onCell: (record: FormatedSemesterType) => ({ rowSpan: getRowSpan(record) }),
        render: (text: any, record: FormatedSemesterType) => (
          <Space>
            {hasPermission('ORGANIZATION_SEMESTER_MANAGEMENT_NEW') &&
              <ReButton iconName='plus' onClick={clickExamCreate(record)}>
                {`${buttonT.NEW}${semesterT.EXAM}`}
              </ReButton>
            }

            {hasPermission('ORGANIZATION_SEMESTER_MANAGEMENT_EDIT') &&
              <ReButton
                iconName='tool'
                onClick={clickSemesterEdit(record)}
                attributeObject={{ type: 'default' }}
              >
                {buttonT.EDIT}
              </ReButton>
            }

            {hasPermission('ORGANIZATION_SEMESTER_MANAGEMENT_DELETE') &&
              <ReButton
                iconName='delete'
                onClick={clickSemesterDelete(record.semesterId)}
                attributeObject={{ type: 'danger' }}
              >
                {buttonT.DELETE}
              </ReButton>
            }

            {hasPermission('BUY_RECORD_EXPORT') &&
              <Dropdown.Button
                menu={{
                  items: [
                    { key: 'transcript', label: menuTitleT.archivedTranscript },
                  ],
                  onClick: clickArchived(record),
                }}
              >
                {buttonT.ARCHIVED}
              </Dropdown.Button>
            }
          </Space>
        ),
      }
    ]
  };

  useEffect(() => {
    getSemestersByYearFunction();
  }, []);

  function formatedData(semesterArray: SemesterType[]) {
    const formatedSemesterArray: FormatedSemesterType[] = [];

    semesterArray.forEach((item: SemesterType) => {
      const { semesterId, year, semesterName, examList } = item;
      const data = { semesterId, year, semesterName, examId: null, examName: null };

      if (examList.length > 0) {
        examList.forEach((exam: ExamType) => {
          const { examId, examName } = exam;

          formatedSemesterArray.push({ ...data, examId, examName });
        });
      } else {
        formatedSemesterArray.push({ ...data });
      }
    });
    return formatedSemesterArray;
  }

  function getSemestersByYearFunction() {
    const { year } = form.getFieldsValue();

    getSemestersByYear({ year }).then((res: any) => {
      const semesterArray = res?.value ?? [];
      setSemesterArray(semesterArray);
      setFormatedSemesterArray(formatedData(semesterArray));
    });
  };

  function clickSemesterCreate() {
    setSemesterInfo({} as FormatedSemesterType);
    toggleModal('createSemester');
  };

  const clickSemesterEdit = (data: FormatedSemesterType) => (e: any) => {
    e.stopPropagation();
    setSemesterInfo(data);
    toggleModal('updateSemester');
  };

  const clickSemesterDelete = (id: number | null) => () => {
    showDelConfirm(() => deleteSemesterFunction(id));
  };

  const clickExamDelete = (id: number | null) => () => {
    showDelConfirm(() => deleteExamFunction(id));
  };

  const clickExamCreate = (record: FormatedSemesterType) => () => {
    setSemesterInfo({ ...record });
    toggleModal('createExam');
  };

  const clickExamEdit = (data: FormatedSemesterType) => (e: any) => {
    e.stopPropagation();
    setSemesterInfo(data);
    toggleModal('updateExam');
  };

  function createSemesterFunction(values: SemesterType) {
    const { semesterName, year } = values;
    const params = { semesterName, year };

    createSemester(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getSemestersByYearFunction();
    });
  };

  function updateSemesterFunction(values: SemesterType) {
    const { semesterId } = semesterInfo;
    const { semesterName, year } = values;
    const examList = semesterArray.find((item: SemesterType) => item.semesterId === semesterId)?.examList;


    const params = { semesterId, semesterName, year, examList };

    if (!hasPermission('ORGANIZATION_SEMESTER_MANAGEMENT_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateSemesterAndExam(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getSemestersByYearFunction();
    });
  }

  function deleteSemesterFunction(id: number | null) {
    const params = { semesterId: id };

    deleteSemester(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getSemestersByYearFunction();
    });
  };

  function createExamFunction(values: ExamType) {
    const { semesterId } = semesterInfo;
    const { examName } = values;
    const params = { examName, semesterId };

    createExam([params]).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getSemestersByYearFunction();
    });
  };

  function updateExamFunction(values: ExamType) {
    const { semesterId, semesterName, year, examId } = semesterInfo;
    const { examName } = values;
    const examList = semesterArray.find((item: SemesterType) => item.semesterId === semesterId)?.examList.map((item: ExamType) => (
      item.examId === examId ? { ...item, examName } : { ...item }
    ));


    const params = { semesterId, semesterName, year, examList };

    if (!hasPermission('ORGANIZATION_SEMESTER_MANAGEMENT_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateSemesterAndExam(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getSemestersByYearFunction();
    });
  }

  function deleteExamFunction(id: number | null) {
    const params = { examId: id };

    deleteExam(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getSemestersByYearFunction();
    });
  };

  const clickArchived = (record: FormatedSemesterType) => (e: any) => {
    const { key } = e;
    const { year, semesterName } = record;
    const params = { year, semester: semesterName };

    switch (key) {
      case 'transcript':
        return updateArchivedTranscript(params).then((res: any) => {
          if (!res.success) {
            return message.error(errorT.failed);
          }
          message.success(errorT.success);
        });
      default:
        return null;
    }
  }

  function toggleModal(handleType: string = '') {
    setIsModalVisible(!isModalVisible);
    setHandleType(handleType);
  };

  return (
    <>
      <Row justify="center">
        <Col>
          <Form
            form={form}
            initialValues={{
              year: getCurrentAcademicYear().toString(),
            }}
          >
            <Form.Item name="year" label={semesterT.YEAR}>
              <Select
                showSearch
                style={{ width: "400px" }}
                optionFilterProp="children"
                filterOption={(input: any, option: any) => selectFilterOption(input, option)}
                onChange={getSemestersByYearFunction}
              >
                {getOptionArray(getDataArray(yearArray, '', ''))}
              </Select>
            </Form.Item>
          </Form>
        </Col>
      </Row>

      <Row gutter={[0, 8]} justify="end">
        <Col>
          {hasPermission('ORGANIZATION_SEMESTER_MANAGEMENT_NEW') &&
            <ReButton iconName='plus' onClick={clickSemesterCreate}>
              {`${buttonT.NEW}${semesterT.SEMESTER}`}
            </ReButton>
          }
        </Col>

        <Col span={24}>
          <ResponsiveTable
            dataSource={formatedSemesterArray}
            columns={getColumns()}
          />
        </Col>
      </Row>

      {isModalVisible && (handleType === 'createSemester' || handleType === 'updateSemester') &&
        <ReModal
          title={semesterT.SUBJECT}
          onCancel={toggleModal}
          visible={isModalVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okSemesterFunction)}
          CollectedForm={ModalSemester}
          formProps={{
            info: semesterInfo,
            handleType,
            yearArray,
          }}
          width={'700px'}
        />
      }

      {isModalVisible && (handleType === 'createExam' || handleType === 'updateExam') &&
        <ReModal
          title={semesterT.SUBJECT}
          onCancel={toggleModal}
          visible={isModalVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okExamFunction)}
          CollectedForm={ModalExam}
          formProps={{
            info: semesterInfo,
            handleType,
            yearArray,
          }}
          width={'700px'}
        />
      }
    </>
  )
}

export default SemesterManagement;